import { resolveId } from '@medplum/core'
import type { DocumentReference, Reference } from '@medplum/fhirtypes'
import {
  DOCUMENT_CATEGORY_OPTIONS,
  DOCUMENT_TYPE_OPTIONS_BY_CATEGORY,
  type DocumentCategory,
  type DocumentTypes,
} from '@seen/core/models/document-references/constants'
import { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import { Order } from '@seen/core/models/orders/Order'
import { Signature } from '@seen/core/models/signature'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { Role } from '@seen/core/roles'
import { queryTypesToString } from '@seen/core/search'
import { Button, cn, Label, Separator } from '@seen/ui'
import { IconChevronRight, IconSparkles } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type JSX, type ReactNode, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import { Activity } from '#src/components/activity/Activity.tsx'
import { ComboboxButton } from '#src/components/ComboboxButton.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import {
  ComboboxMulti,
  type SuggestedMultiOption,
} from '#src/components/core/combobox/ComboboxMulti.tsx'
import { MultiSelect } from '#src/components/core/combobox/MultiSelect.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DocumentReferenceActivity } from '#src/components/document-reference/DocumentReferenceActivity.tsx'
import { useFolders } from '#src/components/document-reference/useFolders.ts'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { ParticipantOption } from '#src/components/options/ParticipantOption.tsx'
import { OrderOption } from '#src/components/OrderOption.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { createTaskFilter } from '#src/hooks/inbox/filter.ts'
import { useMarkRelatedInboxItemsAsDone } from '#src/hooks/inbox/useMarkRelatedInboxItemsAsDone.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useTransactionMutation } from '#src/hooks/useFhirBundleMutation.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useReference } from '#src/hooks/useReference.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

function SuggestedOptionTooltip({
  confidence,
  reason,
}: {
  confidence?: number
  reason?: string
}): ReactNode {
  return (
    <Tooltip>
      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex">
          <IconSparkles className="size-4 text-green-600" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <div className="flex flex-col gap-1">
          {confidence !== undefined && (
            <span className="font-medium">
              {Math.round(confidence * 100)}% confidence
            </span>
          )}
          {reason && <span>{reason}</span>}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function getSuggestedOrderOptions(
  predictedOrders: ExtendedDocumentReference['predictedOrders'],
  orders: Order[] | undefined
): SuggestedMultiOption<string>[] {
  if (!predictedOrders?.length) {
    return []
  }

  const suggestions: SuggestedMultiOption<string>[] = []

  for (const p of predictedOrders) {
    const orderId = resolveId(p.reference)
    if (!orderId) {
      continue
    }

    const order = orders?.find((o) => o.id === orderId)
    if (!order) {
      continue
    }

    suggestions.push({
      label: <OrderOption order={p.reference} />,
      value: orderId,
      keywords: order.searchKeywords ?? [],
      tooltip: (
        <SuggestedOptionTooltip confidence={p.confidence} reason={p.reason} />
      ),
    })
  }

  return suggestions
}

export function DocumentPickerDetail({
  documentId,
}: {
  documentId: string | null | undefined
}): ReactNode {
  if (!documentId) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Select a document to view details
      </div>
    )
  }
  return <DocumentPickerDetailInner documentId={documentId} />
}

function DocumentPickerDetailInner({ documentId }: { documentId: string }) {
  const documentReference = useReference<DocumentReference>(
    `DocumentReference/${documentId}`
  )
  return (
    <DocumentPickerDetailInnerInner documentReference={documentReference} />
  )
}

function DocumentPickerDetailInnerInner({
  documentReference,
  hideParticipant = false,
  onReviewComplete,
}: {
  documentReference: Reference<DocumentReference>
  hideParticipant?: boolean
  onReviewComplete?: (documentReference: Reference<DocumentReference>) => void
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const folders = useFolders()
  const { data: docRef, isLoading } = useRead(
    ExtendedDocumentReference,
    documentReference
  )
  const transactionMutation = useTransactionMutation()
  const { mutate: mutateTransaction } = transactionMutation
  const currentPractitioner = useCurrentPractitioner()
  const { roles } = useCurrentPractitionerPerms()
  const isPCP = roles.includes(Role.PCP)

  const participants = useParticipants()

  const patient = docRef?.subject
  const { data: orders } = useSearch(
    Order,
    Order.byNonDraftOrCancelled(patient),
    { query: { enabled: !!patient } }
  )
  const linkedOrders = useSearch(
    Order,
    {
      _id: docRef?.orders?.map(resolveId) ?? [],
    },
    { query: { enabled: !!docRef?.orders?.length } }
  )

  const { data: signatures = [] } = useSearch(
    Signature,
    docRef ? Signature.queryByTarget(docRef.reference.reference) : undefined,
    { query: { enabled: !!docRef } }
  )

  const { data: reviewTask } = useSearchOne(
    DocumentReviewTask,
    docRef ? DocumentReviewTask.byDocument(docRef.reference) : undefined,
    { query: { enabled: !!docRef } }
  )

  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  const hasCurrentPractitionerSigned = useMemo(() => {
    if (!docRef || !isPCP) {
      return false
    }
    return ExtendedDocumentReference.hasPractitionerSigned(
      signatures,
      currentPractitioner.reference
    )
  }, [docRef, isPCP, signatures, currentPractitioner.reference])

  const signButtonText = hasCurrentPractitionerSigned
    ? 'Signed'
    : 'Sign Document'

  const handleSign = useCallback(() => {
    if (!docRef) {
      return
    }

    const signature = new Signature({
      target: docRef.reference,
      who: currentPractitioner.reference,
    })

    mutateTransaction(
      (transaction) => {
        transaction.create(signature, {
          request: {
            ifNoneExist: queryTypesToString(
              Signature.queryByTargetAndWho(
                docRef.reference,
                currentPractitioner.reference
              )
            ),
          },
        })
        transaction.patch(docRef, (doc) => {
          doc.markAsConfirmed()
        })
        if (reviewTask) {
          transaction.patch(reviewTask, (t) => {
            t.state = 'done'
            if (docRef.subject) {
              t.participant = docRef.subject
            }
          })
        }
      },
      {
        onSuccess: () => {
          toast.success('Document signed successfully')
          if (reviewTask) {
            markRelatedInboxItemsAsDone(
              createTaskFilter([
                {
                  taskReferenceString: reviewTask.reference.reference,
                  state: 'done',
                },
              ])
            )
          }
          onReviewComplete?.(documentReference)
        },
        onError: (error) => {
          console.error('Failed to sign document', error)
          toast.error('Failed to sign document')
        },
      }
    )
  }, [
    docRef,
    currentPractitioner.reference,
    mutateTransaction,
    reviewTask,
    markRelatedInboxItemsAsDone,
    onReviewComplete,
    documentReference,
  ])

  const documentTypeOptions = useMemo(() => {
    if (!docRef) {
      return undefined
    }
    const allOptions = docRef.categories.flatMap(
      (category) => DOCUMENT_TYPE_OPTIONS_BY_CATEGORY[category] ?? []
    )
    return allOptions.length > 0 ? allOptions : undefined
  }, [docRef])

  const suggestedOrderOptions = useMemo(
    () => getSuggestedOrderOptions(docRef?.predictedOrders, orders),
    [docRef?.predictedOrders, orders]
  )

  if (isLoading || !docRef) {
    return <Loader />
  }

  return (
    <ScrollArea scrollRestorationId={null}>
      <div className="space-y-4 flex flex-col pt-4">
        <h5 className="px-4">Properties</h5>
        <div className="flex flex-wrap gap-4 px-4">
          <div className="flex flex-col gap-2">
            <Label>Name</Label>
            <span className="text-sm">{docRef.title}</span>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Date</Label>
            <span className="text-sm">
              {docRef.date.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: '2-digit',
              })}
            </span>
          </div>

          {!hideParticipant && (
            <div className="flex flex-col gap-2">
              <Label>Participant</Label>
              <ResourceFieldForm
                instance={docRef}
                valueFn={(docRef) => docRef.subject}
                patchFn={(docRef, value) => {
                  docRef.subject = value ?? undefined
                }}
                render={({ field: { value, onChange, ...fieldRest } }) => (
                  <Combobox
                    options={participants.map((p) => ({
                      label: <ParticipantOption participant={p.reference} />,
                      value: p.id,
                      keywords: p.searchKeywords,
                    }))}
                    onSelect={(value) => {
                      onChange(
                        participants.find((p) => p.id === value)?.reference ??
                          null
                      )
                    }}
                    {...fieldRest}
                  >
                    <Button variant="outline">
                      {value ? (
                        <ParticipantNameplate participant={value} />
                      ) : (
                        <UserPlaceholderNameplate>
                          Participant
                        </UserPlaceholderNameplate>
                      )}
                    </Button>
                  </Combobox>
                )}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label>Categories</Label>
            <ResourceFieldForm
              instance={docRef}
              valueFn={(docRef) => docRef.categories}
              patchFn={(docRef, value) => {
                docRef.categories = value ?? []
              }}
              render={({ field: { value, onChange, ...fieldRest } }) => (
                <MultiSelect
                  options={DOCUMENT_CATEGORY_OPTIONS}
                  value={value}
                  onChange={(values) => onChange(values as DocumentCategory[])}
                  placeholder="Select categories…"
                  variant="outline"
                  {...fieldRest}
                />
              )}
            />
          </div>

          {folders.length > 0 && (
            <div className="flex flex-col gap-2">
              <Label>Folder</Label>
              <ResourceFieldForm
                instance={docRef}
                valueFn={(docRef) => docRef.folder}
                patchFn={(docRef, value) => {
                  docRef.folder = value ?? undefined
                }}
                render={({ field: { value, onChange, ...fieldRest } }) => (
                  <ComboboxButton
                    value={value?.id}
                    onChange={(value) =>
                      onChange(folders.find((f) => f.id === value) ?? null)
                    }
                    options={[
                      {
                        value: 'other',
                        label: 'Other',
                        keywords: ['Other'],
                      },
                      ...folders.map((folder) => ({
                        value: folder.id,
                        label: folder.name,
                        keywords: [folder.name],
                      })),
                    ]}
                    {...fieldRest}
                  />
                )}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label>Orders</Label>
            <div className="flex items-center">
              <ResourceFieldForm
                instance={docRef}
                valueFn={(docRef) => docRef.orders}
                patchFn={(docRef, value) => {
                  docRef.orders = value ?? undefined
                }}
                render={({ field: { value: _, onChange, ...fieldRest } }) => (
                  <ComboboxMulti
                    options={
                      orders?.map((o) => ({
                        label: <OrderOption order={o.reference} />,
                        value: o.id,
                        keywords: o.searchKeywords,
                      })) ?? []
                    }
                    suggestedOptions={suggestedOrderOptions}
                    suggestedLabel="AI Suggested"
                    onSelect={(values) => {
                      const selectedOrders = orders?.filter((o) =>
                        values.includes(o.id)
                      )
                      onChange(selectedOrders?.map((o) => o.reference) ?? [])
                    }}
                    values={compact(docRef?.orders?.map(resolveId) ?? [])}
                    className="w-auto"
                  >
                    <Button
                      variant="outline"
                      className={cn(docRef?.orders?.length && 'rounded-r-none')}
                      {...fieldRest}
                    >
                      {linkedOrders?.data?.length && docRef?.orders?.length
                        ? linkedOrders?.data
                            ?.map((o) => o.seenIdentifier)
                            .join(', ')
                        : 'Link Orders'}
                    </Button>
                  </ComboboxMulti>
                )}
              />
              {(linkedOrders?.data?.length ?? 0) === 1 && (
                <Link
                  to={`/Patient/${resolveId(linkedOrders?.data?.[0]?.participant)}/orders/${linkedOrders?.data?.[0]?.id}`}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    className="rounded-l-none border-l-0"
                  >
                    <IconChevronRight className="size-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>

          {!isReadOnly && docRef.subject && (
            <div className="flex flex-col gap-2">
              <Label>PCP Review</Label>
              <Button
                variant="outline"
                onClick={handleSign}
                disabled={
                  !isPCP ||
                  hasCurrentPractitionerSigned ||
                  transactionMutation.isPending
                }
              >
                {signButtonText}
              </Button>
            </div>
          )}

          {documentTypeOptions?.length && (
            <div className="flex flex-col gap-2">
              <Label>Document Type</Label>
              <ResourceFieldForm
                instance={docRef}
                valueFn={(docRef) => docRef.typeCode}
                patchFn={(docRef, value) => {
                  docRef.typeCode = value ?? undefined
                }}
                render={({ field: { value, onChange, ...fieldRest } }) => (
                  <ComboboxButton
                    value={value}
                    onChange={(value) => onChange(value as DocumentTypes)}
                    options={documentTypeOptions}
                    {...fieldRest}
                  />
                )}
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 px-4">
          <Label>Description</Label>
          <ResourceFieldForm
            instance={docRef}
            valueFn={(docRef) => docRef.description}
            patchFn={(docRef, value) => {
              docRef.description = value
            }}
            debounceTyping
            render={({ field: { value, ...fieldRest } }) => (
              <RichTextEditor
                defaultValue={value}
                placeholder="Description"
                variant="unstyled"
                {...fieldRest}
              />
            )}
          />
        </div>

        {docRef.aiSummary && (
          <div className="flex flex-col gap-2 px-4">
            <Label>AI Summary</Label>
            <RichText value={docRef.aiSummary} className="text-sm" />
          </div>
        )}

        <Separator />

        <Activity
          resource={docRef}
          subject={docRef.subject}
          className="px-4 pb-4"
        >
          <DocumentReferenceActivity documentReference={docRef} />
        </Activity>
      </div>
    </ScrollArea>
  )
}
